import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";
import { X } from "lucide-react";

export const Modal = ({
  visible,
  isOpen,
  onHide,
  onClose,
  header,
  title,
  footer,
  children,
  className,
  contentClassName,
  style,
  maxWidth,
  dismissableMask = true,
  closeOnEscape = true,
  closable = true,
}) => {
  const [mounted, setMounted] = useState(false);
  const isVisible = visible !== undefined ? Boolean(visible) : Boolean(isOpen);
  const handleClose = onHide || onClose;
  const headerContent = header !== undefined ? header : title;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape" && closeOnEscape && handleClose) {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isVisible, closeOnEscape, handleClose]);

  if (!isVisible || !mounted) return null;

  const modalNode = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm transition-opacity"
        onClick={() => {
          if (dismissableMask && handleClose) handleClose();
        }}
      />

      {/* Modal Dialog Content */}
      <div
        style={style}
        className={cn(
          "relative z-10 w-full",
          maxWidth || "max-w-lg",
          "bg-urb-card border border-urb-border rounded-2xl shadow-2xl text-urb-text overflow-hidden my-auto animate-in fade-in zoom-in-95 duration-200",
          className
        )}
      >
        {/* Header */}
        {headerContent && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-urb-border/60 bg-urb-card-light/50">
            <div className="text-lg font-bold text-white">
              {headerContent}
            </div>
            {closable && handleClose && (
              <button
                type="button"
                onClick={handleClose}
                className="text-urb-text-muted hover:text-white rounded-lg p-1.5 transition-colors cursor-pointer bg-transparent border-none"
                aria-label="Fechar"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div
          className={cn(
            className?.includes("p-0") ? "p-0" : "p-6",
            "max-h-[calc(92vh-60px)] overflow-y-auto",
            contentClassName
          )}
        >
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="px-6 py-4 bg-urb-card-light/40 border-t border-urb-border/50 flex items-center justify-end gap-3">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalNode, document.body);
};

export const Dialog = Modal;
export default Modal;
